import React, { useRef, useState } from "react";
import clsx from "clsx";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

import { roomColor } from "../FilterBar";
import { isOffShift } from "./rowUtils";
import PopoverPortal from "./cells/PopoverPortal";
import { payloadForRole } from "./crewRoles";
import RolePicker from "./RolePicker";

// Operational windows — chips only make sense for short, recent gaps.
const MAX_GAP_MIN = 240;      // 4h
const MAX_TRANSFER_MIN = 240; // 4h
const GAP_MIN_MIN = 30;

/**
 * One crew member's cell for a given block row.
 *
 * Click behavior:
 *   - Click empty cell → role picker popover (project-configured roles + add)
 *   - Click assigned chip → instantly removes (no confirm), per user choice
 *
 * Display rule:
 *   - is_lead → "LEAD" text
 *   - any other role → solid dot
 *
 * The parent handles double-book detection + confirm (see CrewPickerHost /
 * Schedule.jsx where onAssignCrew is wired).
 */
export default function CrewCell({
  crewMember,
  block,
  blockStartMs,
  blockEndMs,
  startDisplay,
  assignment,
  prev,
  next,
  room,
  roomById,
  cellBase,
  stateStyle,
  rowH,
  onSelect,
  onContextMenu,
  onAssignCrew,
  onUnassignCrew,
  doubleBookKeys,
  isLocked,
  crewRoles,
  onAddCrewRole,
}) {
  const a = assignment;
  const [pickerOpen, setPickerOpen] = useState(false);
  const cellRef = useRef(null);

  // Has this crew member been explicitly double-booked on this block (i.e.,
  // user overrode the confirm)? If so, render the amber "floater" stripe
  // overlay and a small ⚠ marker next to the role chip.
  const hasDoubleBook = !!(a && doubleBookKeys && doubleBookKeysContains(doubleBookKeys, crewMember.id, block.id));

  const travelMin = a && prev ? Math.max(0, Math.round((blockStartMs - prev.end_ms) / 60000)) : 0;
  const showTransfer =
    !!(a && prev && prev.room_id !== block.room_id && travelMin > 0 && travelMin <= MAX_TRANSFER_MIN);
  const fromRoom = showTransfer ? roomById?.[prev.room_id] : null;

  const gapMin = !a && next ? Math.round((next.start_ms - blockStartMs) / 60000) : 0;
  const showGap = !a && gapMin >= GAP_MIN_MIN && gapMin <= MAX_GAP_MIN;

  const cellOff = !a && isOffShift(crewMember, startDisplay);
  const dateKey = startDisplay?.slice?.(0, 10) || "";
  const isNA = !!crewMember.unavailable_until && dateKey && dateKey <= crewMember.unavailable_until;
  const canEdit = !isLocked && !!(onAssignCrew && onUnassignCrew);

  const handleCellClick = (e) => {
    // Don't swallow row-selection clicks (modifier keys etc.) — only the
    // bare-left-click on this cell should toggle the picker / unassign.
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      onSelect?.(e);
      return;
    }
    if (!canEdit) {
      onSelect?.(e);
      return;
    }
    e.stopPropagation();
    if (a) {
      // Instant remove, no confirm — per spec
      onUnassignCrew(a.id);
    } else {
      if (isNA) {
        toast.warning(`${crewMember.name} is marked unavailable until ${crewMember.unavailable_until}`);
      }
      setPickerOpen(true);
    }
  };

  const pick = async (roleKey) => {
    setPickerOpen(false);
    await onAssignCrew(block.id, crewMember.id, payloadForRole(roleKey));
  };

  return (
    <div
      ref={cellRef}
      className={clsx(
        cellBase,
        "bg-bg-base relative",
        stateStyle,
        cellOff && "crew-off",
        canEdit && !a && "cursor-pointer hover:bg-bg-elevated/60 transition-colors",
        isNA && !a && "opacity-50",
        hasDoubleBook && "ring-1 ring-amber/40"
      )}
      style={{ height: rowH }}
      onClick={handleCellClick}
      onContextMenu={onContextMenu}
      title={isNA && !a ? `${crewMember.name} — unavailable until ${crewMember.unavailable_until}` : undefined}
      data-testid={`row-${block.id}-crew-${crewMember.id}`}
    >
      {hasDoubleBook && (
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-px bg-amber"
          style={{ boxShadow: "0 0 4px rgba(255,179,0,0.7)" }}
        />
      )}
      {hasDoubleBook && (
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-px bg-amber"
          style={{ boxShadow: "0 0 4px rgba(255,179,0,0.7)" }}
        />
      )}
      {a ? (
        <div className="flex items-center gap-1 w-full min-w-0">
          {showTransfer && (
            <span
              className="mono text-2xs flex items-center gap-0.5 text-cyan shrink-0"
              title={`Transfer from ${fromRoom?.abbreviation || fromRoom?.name || "prev room"} · ${travelMin}m turnaround`}
              data-testid={`xfer-${block.id}-${crewMember.id}`}
            >
              <ArrowUpRight className="w-2.5 h-2.5" />
              {travelMin}m
            </span>
          )}
          <span
            className="crew-pill"
            style={{
              background: roomColor(room?.color_token) + "26",
              color: roomColor(room?.color_token),
              border: `1px solid ${roomColor(room?.color_token)}66`,
            }}
            title={canEdit ? `${crewMember.name} — click to remove` : `${crewMember.name}`}
          >
            {a.is_lead ? "LEAD" : <span aria-hidden style={{ fontSize: "1.1em", lineHeight: 1 }}>●</span>}
          </span>
        </div>
      ) : showGap ? (
        <span
          className="mono text-2xs tracking-wider text-ink-mute opacity-70"
          title={`${crewMember.name} free for ${gapMin}m before next assignment`}
          data-testid={`free-${block.id}-${crewMember.id}`}
        >
          free {gapMin}m
        </span>
      ) : null}
      <PopoverPortal anchorRef={cellRef} open={pickerOpen} onClose={() => setPickerOpen(false)}>
        <RolePicker
          crewMember={crewMember}
          isNA={isNA}
          crewRoles={crewRoles}
          onPick={pick}
          onAddRole={onAddCrewRole}
        />
      </PopoverPortal>
    </div>
  );
}

// Returns true if `set` contains a key referencing this crew + block. Keys are
// produced by `makeDoubleBookKey` in Schedule.jsx — `${crew}|${blockA}|${blockB}`
// (block ids sorted), so we accept a match in either position.
function doubleBookKeysContains(set, crewId, blockId) {
  if (!set || set.size === 0) return false;
  const prefix = `${crewId}|`;
  for (const k of set) {
    if (!k.startsWith(prefix)) continue;
    const [, a, b] = k.split("|");
    if (a === blockId || b === blockId) return true;
  }
  return false;
}
